import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import PublicLayout from "@/layouts/PublicLayout";
import { api } from "@/lib/api";
import { CheckCircle2, XCircle, Loader2, Mail, ArrowLeft } from "lucide-react";

const SUCCESS = {
  fr: { title: "Inscription confirmée 🎉", body: "Merci, vous êtes maintenant inscrit·e à la newsletter mensuelle de La WineTech. Rendez-vous très bientôt dans votre boîte mail !", back: "Retour à La WineTech" },
  en: { title: "Subscription confirmed 🎉", body: "Thank you — you're now subscribed to the La WineTech monthly newsletter. See you in your inbox very soon!", back: "Back to La WineTech" },
  es: { title: "Suscripción confirmada 🎉", body: "Gracias, ya estás suscrito·a al boletín mensual de La WineTech. ¡Nos vemos pronto en tu bandeja de entrada!", back: "Volver a La WineTech" },
  it: { title: "Iscrizione confermata 🎉", body: "Grazie! Sei iscritto·a alla newsletter mensile di La WineTech. Ci vediamo presto nella tua casella di posta!", back: "Torna a La WineTech" },
  pt: { title: "Inscrição confirmada 🎉", body: "Obrigado — está agora inscrito·a na newsletter mensal de La WineTech. Em breve na sua caixa de entrada!", back: "Voltar a La WineTech" },
  de: { title: "Anmeldung bestätigt 🎉", body: "Vielen Dank — Sie sind jetzt für den monatlichen La WineTech-Newsletter angemeldet. Bis bald in Ihrem Posteingang!", back: "Zurück zu La WineTech" },
};

export default function NewsletterConfirm() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [status, setStatus] = useState("loading"); // loading | success | error
  const [lang, setLang] = useState("fr");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) { setStatus("error"); setError("Lien invalide."); return; }
    let alive = true;
    api.get(`/public/newsletter/confirm?token=${encodeURIComponent(token)}`)
      .then((r) => { if (alive) { setStatus("success"); setLang(r.data?.lang || "fr"); } })
      .catch((err) => { if (alive) { setStatus("error"); setError(err.response?.data?.detail || "Lien invalide ou expiré."); } });
    return () => { alive = false; };
  }, [token]);

  const t = SUCCESS[lang] || SUCCESS.fr;

  return (
    <PublicLayout>
      <div className="mx-auto max-w-2xl px-4 py-20 text-center" data-testid="newsletter-confirm">
        {status === "loading" && (
          <div className="rounded-3xl border border-neutral-100 bg-white p-12 shadow-lg">
            <Loader2 className="mx-auto h-12 w-12 animate-spin text-wine-500" />
            <p className="mt-4 text-neutral-500">Confirmation en cours…</p>
          </div>
        )}
        {status === "success" && (
          <div className="rounded-3xl border border-emerald-100 bg-emerald-50/40 p-12 shadow-lg" data-testid="newsletter-confirm-success">
            <div className="mx-auto grid h-20 w-20 place-items-center rounded-full bg-emerald-500 shadow-xl">
              <CheckCircle2 className="h-12 w-12 text-white" />
            </div>
            <h1 className="mt-6 font-serif text-4xl text-navy">{t.title}</h1>
            <p className="mt-3 text-lg text-neutral-700">{t.body}</p>
            <Link to="/" className="mt-8 inline-flex items-center gap-1.5 rounded-full bg-wine-500 px-6 py-3 text-sm font-bold text-white shadow hover:bg-wine-600">
              <ArrowLeft className="h-4 w-4" /> {t.back}
            </Link>
          </div>
        )}
        {status === "error" && (
          <div className="rounded-3xl border border-rose-100 bg-rose-50/40 p-12 shadow-lg" data-testid="newsletter-confirm-error">
            <div className="mx-auto grid h-20 w-20 place-items-center rounded-full bg-rose-500 shadow-xl">
              <XCircle className="h-12 w-12 text-white" />
            </div>
            <h1 className="mt-6 font-serif text-3xl text-navy">Lien invalide</h1>
            <p className="mt-3 text-neutral-700">{error || "Ce lien d'invitation n'est plus valide ou a déjà été utilisé."}</p>
            <Link to="/" className="mt-8 inline-flex items-center gap-1.5 rounded-full bg-wine-500 px-6 py-3 text-sm font-bold text-white shadow hover:bg-wine-600">
              <Mail className="h-4 w-4" /> Retour à l&apos;accueil
            </Link>
          </div>
        )}
      </div>
    </PublicLayout>
  );
}
